import { forwardRef, useEffect, useId, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { useLockBodyScroll } from 'react-use';
import Button from '@/components/inputs/button';
import { VARIANT as BUTTON_VARIANT } from '@/components/inputs/button/button.types';
import Icon from '@/components/data-display/icon';
import trackEvent from '@/utils/track-event/track-event';
import { EVENT_NAME } from '@/utils/track-event/track-event.types';
import { createPortal } from 'react-dom';
import { FlyoutProvider, useFlyoutContext } from './flyout.context';
import { VARIANT as ICON_VARIANT } from '../data-display/icon/icon.types';
import type {
  FlyoutContentProperties,
  FlyoutProperties,
  FlyoutTriggerProperties,
} from './flyout.types';
import styles from './flyout.module.scss';

export const Flyout = ({ label, children }: FlyoutProperties) => {
  const id = useId();
  const [isOpen, setIsOpen] = useState<boolean>(false);

  useLockBodyScroll(isOpen);

  const contextValue = {
    id,
    isOpen,
    label,
    toggle: () => setIsOpen(!isOpen),
  };

  return <FlyoutProvider value={contextValue}>{children}</FlyoutProvider>;
};

const FlyoutTrigger = ({
  children,
  ...properties
}: FlyoutTriggerProperties) => {
  const { id, isOpen, toggle, label } = useFlyoutContext();

  const handleClick = () => {
    toggle();
    trackEvent({
      event: EVENT_NAME.NAVIGATION,
      nav_element: properties.GAEventReference,
    });
  };

  return (
    <Button
      onClick={handleClick}
      aria-controls={`flyout${id}body`}
      id={`flyout${id}`}
      aria-label={`Open ${label} flyout`}
      aria-expanded={isOpen}
      {...properties}
    >
      {children}
    </Button>
  );
};

const FlyoutContent = forwardRef<HTMLDivElement, FlyoutContentProperties>(
  ({ children, ...properties }, reference) => {
    const { id, isOpen, toggle, label } = useFlyoutContext();
    const containerReference = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
      const mainElement = document.querySelector('main');

      if (!mainElement) return;

      const container = document.createElement('div');
      mainElement.append(container);
      containerReference.current = container;

      // eslint-disable-next-line consistent-return
      return () => {
        if (containerReference.current) {
          containerReference.current.remove();
        }
      };
    }, []);

    if (!containerReference.current) return null;

    return createPortal(
      <CSSTransition
        unmountOnExit
        in={isOpen}
        classNames={{
          enter: styles['flyout--enter'],
          enterDone: styles['flyout--enter-done'],
          exitActive: styles['flyout--exit-active'],
        }}
        timeout={{ enter: 0, exit: 600 }}
      >
        <aside
          className={styles.flyout}
          role="dialog"
          id={`flyout${id}body`}
          aria-label={`${label} flyout`}
        >
          <Button
            className={styles['flyout__close-pane']}
            onClick={() => toggle()}
            aria-label={`Close ${label} flyout`}
            tabIndex={0}
            variant={BUTTON_VARIANT.Unstyled}
          >
            <Icon variant={ICON_VARIANT.X} className={styles.flyout__close} />
          </Button>

          <div
            className={styles.flyout__content}
            ref={reference}
            {...properties}
            onTouchMove={event => event.stopPropagation()}
          >
            {children}
          </div>
        </aside>
      </CSSTransition>,
      containerReference.current,
    );
  },
);
FlyoutContent.displayName = 'FlyoutContent';

Flyout.Trigger = FlyoutTrigger;

Flyout.Content = FlyoutContent;

export default Flyout;
